import { useRef, useSyncExternalStore } from "react";
import { shallowEqual } from "../shallowEqual";
import type { ComposerManager } from "./ComposerManager";
import type { ComposerSnapshot } from "./types";
import { useComposerContext } from "./ComposerProvider";

/**
 * Subscribe to a slice of composer state with shallow-equal memoization.
 * Uses the ComposerManager from context.
 *
 * @example
 * ```tsx
 * const text = useComposerSelector((s) => s.text);
 * const isReady = useComposerSelector((s) => s.isReady);
 * const { setText, sendMessage } = useComposerSelector((s) => ({
 *   setText: s.setText,
 *   sendMessage: s.sendMessage,
 * }));
 * ```
 */
export function useComposerSelector<T>(
  selector: (snapshot: ComposerSnapshot) => T,
): T {
  const composer = useComposerContext();
  return useComposerSelectorWithManager(composer, selector);
}

/**
 * Subscribe to a slice of composer state with shallow-equal memoization.
 * Accepts a ComposerManager directly (no context needed).
 */
export function useComposerSelectorWithManager<T, TMeta extends Record<string, unknown> = Record<string, unknown>>(
  manager: ComposerManager<TMeta>,
  selector: (snapshot: ComposerSnapshot<TMeta>) => T,
): T {
  const prevRef = useRef<T | undefined>(undefined);

  return useSyncExternalStore(
    manager.subscribe,
    () => {
      const next = selector(manager.getSnapshot());
      if (prevRef.current !== undefined && shallowEqual(prevRef.current, next)) {
        return prevRef.current;
      }
      prevRef.current = next;
      return next;
    },
  );
}
